
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Separator } from "@/components/ui/separator";
import { Market, availableMarkets } from "./marketData";
import { MarketList } from "./MarketList";
import { useMarketSelection } from "./useMarketSelection";

interface MarketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (selectedMarkets: Market[]) => Promise<void>;
  currentDefaultMarket: string;
  currentActiveMarkets?: string[];
}

export const MarketDialog: React.FC<MarketDialogProps> = ({ 
  open, 
  onOpenChange, 
  onSave,
  currentDefaultMarket,
  currentActiveMarkets = []
}) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  
  const {
    selectedMarkets,
    defaultMarket,
    activeMarkets,
    handleCheckboxChange,
    handleDefaultChange,
    handleActiveChange
  } = useMarketSelection({
    currentDefaultMarket,
    currentActiveMarkets,
    open
  });

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Prepare selected markets with default flag and active state
      const marketsToSave = availableMarkets
        .filter(market => selectedMarkets.includes(market.country))
        .map(market => ({
          ...market,
          isDefault: market.country === defaultMarket,
          isActive: activeMarkets.includes(market.country)
        }));
      
      await onSave(marketsToSave);
      onOpenChange(false);
      toast({
        title: "Marchés mis à jour",
        description: "Les marchés et devises ont été mis à jour avec succès.",
      });
    } catch (error) {
      console.error("Error saving markets:", error);
      toast({
        title: "Erreur",
        description: "Une erreur s'est produite lors de la mise à jour des marchés.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white dark:bg-gray-900">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center justify-between">
            Ajouter Un Marché
            <button 
              onClick={() => onOpenChange(false)}
              className="rounded-full h-6 w-6 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="h-4 w-4" />
            </button>
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
            Sélectionnez les marchés que vous souhaitez activer et définissez un marché par défaut.
          </DialogDescription>
        </DialogHeader>
        
        <MarketList
          selectedMarkets={selectedMarkets}
          defaultMarket={defaultMarket}
          activeMarkets={activeMarkets}
          onCheckboxChange={handleCheckboxChange}
          onDefaultChange={handleDefaultChange}
          onActiveChange={handleActiveChange}
        />

        <Separator className="my-2" />
        
        <DialogFooter className="flex justify-between sm:justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            Annuler
          </Button>
          <Button 
            onClick={handleSave}
            disabled={isSaving || selectedMarkets.length === 0 || !defaultMarket}
            className="w-full sm:w-auto bg-custom-primary hover:bg-custom-dark dark:bg-[#a78bfa] dark:text-black dark:hover:bg-[#8b5cf6]"
          >
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
